import { useCallback, useEffect, useState } from "react";
import CreateQuizPage from "./CreateQuizPage";
import Pagination, { QUIZ_LIST_PAGE_SIZE } from "../components/Pagination";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const AUTHOR_QUIZZES_API_URL = process.env.REACT_APP_AUTHOR_QUIZZES_API_URL
    || apiUrl("author_quizzes.php");
const AUTHOR_QUIZ_API_URL = process.env.REACT_APP_AUTHOR_QUIZ_API_URL
    || apiUrl("author_quiz.php");

async function readJsonResponse(response) {
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || `Quiz author API returned HTTP ${response.status}`);
    }
    return result;
}

export default function QuizAuthorPage({ text, onBack, onCreated }) {
    const [data, setData] = useState({ quizzes: [], categories: [], reports: [] });
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [reload, setReload] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const [editor, setEditor] = useState(null);
    const [analysisQuizId, setAnalysisQuizId] = useState(null);
    const [quizPage, setQuizPage] = useState(1);

    const loadQuizzes = useCallback(async (signal) => {
        setIsLoading(true);
        setError("");
        try {
            const response = await fetch(AUTHOR_QUIZZES_API_URL, { signal });
            const result = await readJsonResponse(response);
            if (
                !Array.isArray(result.quizzes)
                || !Array.isArray(result.categories)
                || !Array.isArray(result.reports)
            ) {
                throw new Error("Quiz author API returned an invalid response");
            }
            setData(result);
        } catch (requestError) {
            if (requestError.name !== "AbortError") {
                console.error("Failed to load authored quizzes:", requestError);
                setError(text.quizAuthorLoadError);
            }
        } finally {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [text]);

    useEffect(() => {
        const controller = new AbortController();
        loadQuizzes(controller.signal);
        return () => controller.abort();
    }, [loadQuizzes, reload]);

    const editQuiz = async (quizId) => {
        setError("");
        try {
            const response = await fetch(
                `${AUTHOR_QUIZ_API_URL}?id=${encodeURIComponent(quizId)}`
            );
            const result = await readJsonResponse(response);
            setEditor(result.quiz);
        } catch (requestError) {
            console.error("Failed to load authored quiz for editing:", requestError);
            setError(text.quizAuthorLoadError);
        }
    };

    if (editor || isCreating) {
        return (
            <CreateQuizPage
                text={text}
                schoolClassrooms={data.schoolClassrooms}
                initialQuiz={editor}
                onBack={() => {
                    setEditor(null);
                    setIsCreating(false);
                }}
                onCreated={() => {
                    setEditor(null);
                    setIsCreating(false);
                    setQuizPage(1);
                    setReload((version) => version + 1);
                    onCreated();
                }}
            />
        );
    }

    const totalStarts = data.quizzes.reduce((total, quiz) => total + quiz.startCount, 0);
    const totalCompletions = data.quizzes.reduce(
        (total, quiz) => total + quiz.completedCount,
        0
    );
    const totalQuestions = data.quizzes.reduce(
        (total, quiz) => total + quiz.questionSum,
        0
    );
    const averagePercent = totalQuestions > 0
        ? Math.round(data.quizzes.reduce(
            (total, quiz) => total + quiz.scoreSum,
            0
        ) / totalQuestions)
        : 0;
    const quizPageCount = Math.ceil(data.quizzes.length / QUIZ_LIST_PAGE_SIZE);
    const currentQuizPage = Math.min(quizPage, Math.max(1, quizPageCount));
    const paginatedQuizzes = data.quizzes.slice(
        (currentQuizPage - 1) * QUIZ_LIST_PAGE_SIZE,
        currentQuizPage * QUIZ_LIST_PAGE_SIZE
    );

    return (
        <main className="admin-page">
            <header className="admin-header">
                <div>
                    <span className="quiz-label">{text.quizAuthorLabel}</span>
                    <h1>{text.myAuthoredQuizzes}</h1>
                    <p>{text.quizAuthorDescription}</p>
                </div>
                <div className="author-page-actions">
                    <button
                        className="quiz-secondary-btn"
                        type="button"
                        onClick={onBack}
                    >
                        {text.backToQuizzes}
                    </button>
                    <button
                        className="quiz-btn"
                        type="button"
                        onClick={() => setIsCreating(true)}
                    >
                        {text.createQuizButton}
                    </button>
                </div>
            </header>

            {error && <p className="auth-error admin-error" role="alert">{error}</p>}
            {isLoading ? (
                <p className="quiz-empty-state" role="status">{text.adminLoading}</p>
            ) : (
                <>
                    <section className="admin-stats quiz-author-stats" aria-label={text.quizAnalytics}>
                        <article className="admin-stat-card">
                            <span>{text.authoredQuizCount}</span>
                            <strong>{data.quizzes.length}</strong>
                        </article>
                        <article className="admin-stat-card">
                            <span>{text.quizStarts}</span>
                            <strong>{totalStarts}</strong>
                        </article>
                        <article className="admin-stat-card">
                            <span>{text.quizCompletions}</span>
                            <strong>{totalCompletions}</strong>
                        </article>
                        <article className="admin-stat-card">
                            <span>{text.averageScore}</span>
                            <strong>{averagePercent}%</strong>
                        </article>
                    </section>

                    <section className="admin-section author-quiz-section">
                        <h2>{text.authorQuestionReports}</h2>
                        {data.reports.length === 0 ? (
                            <p className="quiz-empty-state">{text.noAuthorQuestionReports}</p>
                        ) : (
                            <div className="admin-list">
                                {data.reports.map((report) => (
                                    <article className="admin-list-item" key={report.id}>
                                        <div className="question-report-details">
                                            <h3>{report.quizTitle}</h3>
                                            <p>{report.questionText}</p>
                                            <p>
                                                {text.reportedBy} {report.reporterName}
                                                {" · "}
                                                <time dateTime={report.createdAt}>
                                                    {new Date(report.createdAt.replace(" ", "T"))
                                                        .toLocaleString()}
                                                </time>
                                            </p>
                                            <span className={
                                                report.status === "open"
                                                    ? "question-report-status open"
                                                    : "question-report-status"
                                            }>
                                                {report.status === "open"
                                                    ? text.reportOpen
                                                    : text.reportResolved}
                                            </span>
                                        </div>
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => editQuiz(report.quizId)}
                                        >
                                            {text.editReportedQuestion}
                                        </button>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="admin-section author-quiz-section">
                        <h2>{text.myAuthoredQuizzes}</h2>
                        {data.quizzes.length === 0 ? (
                            <p className="quiz-empty-state">{text.noAuthoredQuizzes}</p>
                        ) : (
                            <div className="admin-list">
                                {paginatedQuizzes.map((quiz) => (
                                    <article className="author-quiz-card" key={quiz.id}>
                                        <div className="author-quiz-heading">
                                            <div>
                                                <span
                                                    className={`quiz-status-badge ${quiz.status === "draft" ? "draft" : "published"}`}
                                                >
                                                    {quiz.status === "draft"
                                                        ? text.draftQuiz
                                                        : text.publishedQuiz}
                                                </span>
                                                {quiz.isNew && (
                                                    <span className="quiz-new-badge">{text.newQuiz}</span>
                                                )}
                                                <p className="author-quiz-classification">
                                                    <span><strong>{text.schoolClassroom}:</strong> {quiz.schoolTrack}</span>
                                                    <span><strong>{text.subjectLabel}:</strong> {quiz.category}</span>
                                                </p>
                                                <h3>{quiz.title}</h3>
                                                <p>{quiz.description}</p>
                                            </div>
                                            <button
                                                className="quiz-secondary-btn"
                                                type="button"
                                                onClick={() => editQuiz(quiz.id)}
                                            >
                                                {text.editQuiz}
                                            </button>
                                        </div>
                                        <div className="author-quiz-metrics">
                                            <div>
                                                <span>{text.quizStarts}</span>
                                                <strong>{quiz.startCount}</strong>
                                            </div>
                                            <div>
                                                <span>{text.quizCompletions}</span>
                                                <strong>{quiz.completedCount}</strong>
                                            </div>
                                            <div>
                                                <span>{text.averageScore}</span>
                                                <strong>{quiz.averagePercent}%</strong>
                                            </div>
                                            <div>
                                                <span>{text.completionRate}</span>
                                                <strong>{quiz.completionPercent}%</strong>
                                            </div>
                                        </div>
                                        <button
                                            className="quiz-secondary-btn quiz-analysis-toggle"
                                            type="button"
                                            aria-expanded={analysisQuizId === quiz.id}
                                            onClick={() => setAnalysisQuizId(
                                                analysisQuizId === quiz.id ? null : quiz.id
                                            )}
                                        >
                                            {analysisQuizId === quiz.id
                                                ? text.hideQuizAnalysis
                                                : text.viewQuizAnalysis}
                                        </button>
                                        {analysisQuizId === quiz.id && (
                                            <section className="quiz-question-analysis">
                                                <h4>{text.questionPerformance}</h4>
                                                {quiz.questionAnalytics.length === 0 ? (
                                                    <p className="quiz-empty-state">
                                                        {text.noQuestionAnalytics}
                                                    </p>
                                                ) : (
                                                    <div className="quiz-question-analysis-list">
                                                        {quiz.questionAnalytics.map((question) => (
                                                            <article
                                                                className="quiz-question-analysis-item"
                                                                key={`${question.position}-${question.questionText}-${question.correctAnswer}`}
                                                            >
                                                                <h5>
                                                                    {text.question} {question.position}:{" "}
                                                                    {question.questionText}
                                                                </h5>
                                                                <p>
                                                                    {text.correctAnswerLabel}:{" "}
                                                                    {question.correctAnswer}
                                                                </p>
                                                                <div
                                                                    className="quiz-question-analysis-bar"
                                                                    role="meter"
                                                                    aria-valuemin="0"
                                                                    aria-valuemax="100"
                                                                    aria-valuenow={question.correctPercent}
                                                                    aria-label={`${question.correctPercent}% ${text.correctAnswersRate}`}
                                                                >
                                                                    <span style={{
                                                                        width: `${question.correctPercent}%`
                                                                    }} />
                                                                </div>
                                                                <div className="quiz-question-analysis-summary">
                                                                    <strong>
                                                                        {question.correctPercent}%{" "}
                                                                        {text.correctAnswersRate}
                                                                    </strong>
                                                                    <span>
                                                                        {question.correctCount} {text.correctAnswers}
                                                                        {" · "}
                                                                        {question.incorrectCount} {text.incorrectAnswers}
                                                                        {" · "}
                                                                        {question.responseCount} {text.responses}
                                                                    </span>
                                                                </div>
                                                            </article>
                                                        ))}
                                                    </div>
                                                )}
                                                <Pagination
                                                    page={currentQuizPage}
                                                    pageSize={QUIZ_LIST_PAGE_SIZE}
                                                    totalItems={data.quizzes.length}
                                                    text={text}
                                                    onPageChange={setQuizPage}
                                                />
                                            </section>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}
        </main>
    );
}
